import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Search, Edit2, Package } from 'lucide-react';
import { materialMasterAPI } from '../../services/api';

const UOM_OPTIONS = ['Kg', 'MT', 'Pcs', 'Mtr', 'Ltr'];
const CURRENCY_OPTIONS = ['INR', 'USD', 'EUR', 'GBP'];

function formatDate(iso) {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}

function formatPrice(val, currency) {
  if (val == null) return '-';
  const sym = currency === 'INR' ? '\u20B9' : currency === 'USD' ? '$' : currency === 'EUR' ? '\u20AC' : '\u00A3';
  return sym + ' ' + Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const EMPTY_FORM = { material_code: '', material_name_grade: '', uom: 'Kg', current_price: '', currency: 'INR' };

export default function MaterialMasterPage() {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const fetchMaterials = useCallback(async () => {
    try {
      const res = await materialMasterAPI.list();
      setMaterials(res.data || []);
    } catch { toast.error('Failed to load materials'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchMaterials(); }, [fetchMaterials]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setDialogOpen(true); };
  const openEdit = (m) => {
    setEditing(m);
    setForm({
      material_code: m.material_code,
      material_name_grade: m.material_name_grade,
      uom: m.uom,
      current_price: String(m.current_price),
      currency: m.currency,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.material_code.trim() || !form.material_name_grade.trim()) {
      toast.error('Code and Name/Grade are required');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, current_price: parseFloat(form.current_price) || 0 };
      if (editing) {
        await materialMasterAPI.update(editing.id, payload);
        toast.success('Material updated');
      } else {
        await materialMasterAPI.create(payload);
        toast.success('Material created');
      }
      setDialogOpen(false);
      fetchMaterials();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await materialMasterAPI.delete(deleteId);
      toast.success('Material deleted');
      setDeleteId(null);
      fetchMaterials();
    } catch { toast.error('Delete failed'); }
  };

  const filtered = materials.filter((m) => {
    const q = search.toLowerCase();
    return !q || m.material_code.toLowerCase().includes(q) || m.material_name_grade.toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="material-master-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold uppercase tracking-tight text-foreground">
            Material Master
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage raw material grades, prices, and units
          </p>
        </div>
        <Button onClick={openCreate} data-testid="add-material-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Material
        </Button>
      </div>

      <Card data-testid="materials-table-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-heading uppercase">Raw Materials</CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search code or grade..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-9"
                data-testid="material-search"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Package className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">{materials.length ? 'No materials match your search' : 'No raw materials yet. Add one to get started.'}</p>
            </div>
          ) : (
            <div className="rounded-md border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40">
                    <TableHead className="font-semibold">Code</TableHead>
                    <TableHead className="font-semibold">Material / Grade</TableHead>
                    <TableHead className="font-semibold text-center">UOM</TableHead>
                    <TableHead className="font-semibold text-right">Price</TableHead>
                    <TableHead className="font-semibold">Price Updated</TableHead>
                    <TableHead className="font-semibold text-center w-24">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((m) => (
                    <TableRow key={m.id} data-testid={`material-row-${m.id}`}>
                      <TableCell>
                        <Badge variant="outline" className="font-mono text-xs">{m.material_code}</Badge>
                      </TableCell>
                      <TableCell className="font-medium">{m.material_name_grade}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="secondary" className="text-xs">{m.uom}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold">
                        {formatPrice(m.current_price, m.currency)}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{formatDate(m.last_updated)}</TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(m)}
                            data-testid={`edit-material-${m.id}`}>
                            <Edit2 className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive"
                            onClick={() => setDeleteId(m.id)} data-testid={`delete-material-${m.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="material-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Material' : 'Add New Material'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase">Material Code</Label>
                <Input value={form.material_code} onChange={(e) => setForm({ ...form, material_code: e.target.value })}
                  placeholder="e.g. RM-10B21" data-testid="input-material-code" className="font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase">Currency</Label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger data-testid="select-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CURRENCY_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase">Material Name / Grade</Label>
              <Input value={form.material_name_grade}
                onChange={(e) => setForm({ ...form, material_name_grade: e.target.value })}
                placeholder="e.g. Boron Steel 10B21" data-testid="input-material-name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase">Unit of Measure</Label>
                <Select value={form.uom} onValueChange={(v) => setForm({ ...form, uom: v })}>
                  <SelectTrigger data-testid="select-uom"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {UOM_OPTIONS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase">Current Price</Label>
                <Input type="number" min={0} step={0.01} value={form.current_price}
                  onChange={(e) => setForm({ ...form, current_price: e.target.value })}
                  placeholder="0.00" data-testid="input-price" className="font-mono" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-material-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Delete Material?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-2">
            This action cannot be undone. The raw material record will be permanently removed.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
